<template>
  <div style="flex: 1; display: flex; flex-direction: column; min-height: 0">
    <div style="flex: 1; display: flex; min-height: 0">
      <!-- set list -->
      <div style="width: 236px; flex: none; display: flex; flex-direction: column; background: var(--back-root); border-right: 1px solid var(--border); min-height: 0">
        <div class="micro" style="padding: 14px 14px 8px">Sets</div>
        <div style="flex: 1; overflow: auto; min-height: 0; padding: 0 12px; display: flex; flex-direction: column; gap: 7px">
          <div
            v-for="(s, i) in sets"
            :key="i"
            item
            horizontal
            align-center
            nowrap
            :selected="i === sel"
            style="height: auto; padding: 9px 11px; gap: 8px"
            @click="sel = i">
            <span nowrap style="flex: 1; font-size: 12.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis">{{ s.label || "Untitled" }}</span>
            <span class="mono" style="font-size: 9.5px; color: var(--hsl-46)">{{ (s.metadata && s.metadata.length) || 0 }}</span>
          </div>
        </div>
        <div style="flex: none; padding: 12px">
          <v-btn small class="addbtn" style="width: 100%" @click="addSet()">+ New set</v-btn>
        </div>
      </div>

      <!-- editor -->
      <div style="flex: 1; display: flex; flex-direction: column; min-width: 0; min-height: 0">
        <template v-if="cur">
          <!-- set meta -->
          <div style="flex: none; padding: 16px 20px; border-bottom: 1px solid var(--border); display: flex; align-items: flex-end; gap: 12px">
            <div style="flex: 1; min-width: 0">
              <div class="field-label">Set name</div>
              <input v-model="cur.label" type="text" class="mv-input" style="width: 100%; font-size: 14px; font-weight: 600" />
            </div>
            <span class="mono" style="font-size: 10px; color: var(--hsl-46); padding-bottom: 10px">{{ plural((cur.metadata && cur.metadata.length) || 0, "field") }}</span>
            <v-btn small @click="duplicateSet()">Duplicate</v-btn>
          </div>
          <!-- fields -->
          <div style="flex: 1; overflow: auto; min-height: 0; padding: 14px 20px 20px">
            <div horizontal align-center style="gap: 8px; margin-bottom: 11px">
              <span class="micro">Fields</span>
              <span style="flex: 1; height: 1px; background: var(--border)"></span>
              <v-btn small class="addbtn" @click="addField()">+ Add field</v-btn>
            </div>
            <div style="display: flex; flex-direction: column; gap: 9px">
              <div
                v-for="(f, fi) in cur.metadata"
                :key="fi"
                style="background: var(--back-header); border: 1px solid var(--border); border-radius: 9px; padding: 11px 12px">
                <div horizontal align-center nowrap style="gap: 9px">
                  <div style="display: flex; flex-direction: column">
                    <v-btn icon x-small :disabled="fi === 0" @click="moveField(fi, -1)"><v-icon size="14">mdi-chevron-up</v-icon></v-btn>
                    <v-btn icon x-small :disabled="fi === cur.metadata.length - 1" @click="moveField(fi, 1)"><v-icon size="14">mdi-chevron-down</v-icon></v-btn>
                  </div>
                  <div style="flex: 1; min-width: 0">
                    <div class="field-label">Label</div>
                    <input v-model="f.label" type="text" class="mv-input" style="width: 100%" />
                  </div>
                  <div style="width: 170px; flex: none">
                    <div class="field-label">Type</div>
                    <select v-model="f.type" class="mv-input" style="width: 100%">
                      <option v-for="t in fieldTypes" :key="t" :value="t">{{ t }}</option>
                    </select>
                  </div>
                  <v-btn icon small class="delbtn" style="align-self: flex-end" title="Delete field" @click="removeField(fi)"><v-icon size="16">mdi-close</v-icon></v-btn>
                </div>
                <div v-if="isListType(f.type)" style="margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--border)">
                  <div class="field-label">Allowed values</div>
                  <div horizontal wrap align-center style="gap: 6px">
                    <span
                      v-for="(v, vi) in f.allowedValues"
                      :key="vi"
                      style="display: inline-flex; align-items: center; gap: 6px; font-size: 11px; background: var(--back-root); border: 1px solid var(--border); padding: 4px 9px; border-radius: 99px">
                      {{ v }}
                      <span class="delbtn" style="cursor: pointer; padding: 0 2px; border-radius: 4px" @click="removeAllowed(f, vi)">✕</span>
                    </span>
                    <input
                      type="text"
                      class="mv-input"
                      placeholder="add value + ⏎"
                      style="flex: 1; min-width: 120px; border-style: dashed"
                      @keyup.enter="addAllowed(f, $event)" />
                  </div>
                </div>
              </div>
              <div v-if="!cur.metadata || !cur.metadata.length" class="mono" style="padding: 30px; text-align: center; color: var(--hsl-40)">
                No fields yet — add one above.
              </div>
            </div>
          </div>
        </template>
        <div v-else class="mono" style="flex: 1; display: flex; align-items: center; justify-content: center; color: var(--hsl-40)">
          Select or create a set.
        </div>
      </div>
    </div>

    <!-- footer -->
    <div footer style="height: 46px; min-height: 46px; max-height: 46px; gap: 10px">
      <span class="mono" style="font-size: 10px; color: var(--hsl-46)">Changes are saved to the server on Save</span>
      <div style="margin-left: auto; display: flex; gap: 8px">
        <v-btn small :disabled="!cur || saving" class="addbtn" @click="save()">{{ saving ? "Saving…" : "Save set" }}</v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { plural } from "@/scripts/plural.js";

export default {
  data() {
    return {
      sets: [],
      sel: 0,
      saving: false,
      fieldTypes: ["STRING", "NUMBER", "DROPDOWN", "TAG", "DATE", "NOTE", "CHECKBOX"],
    };
  },
  computed: {
    cur() {
      return this.sets[this.sel] || null;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    plural,
    load() {
      this.sets = JSON.parse(JSON.stringify(this.$store.state.metadataSets || []));
      this.sel = this.sets.length ? 0 : -1;
    },
    isListType(type) {
      return type === "DROPDOWN" || type === "TAG";
    },
    addSet() {
      this.sets.push({ label: "New set", metadata: [] });
      this.sel = this.sets.length - 1;
    },
    duplicateSet() {
      if (!this.cur) return;
      const copy = JSON.parse(JSON.stringify(this.cur));
      delete copy.id;
      copy.label = this.cur.label + " (copy)";
      this.sets.push(copy);
      this.sel = this.sets.length - 1;
    },
    addField() {
      if (!this.cur) return;
      if (!this.cur.metadata) this.$set(this.cur, "metadata", []);
      this.cur.metadata.push({ id: this.$store.state.getRandomUuid(), label: "Field", type: "STRING", allowedValues: [] });
    },
    removeField(fi) {
      this.cur.metadata.splice(fi, 1);
    },
    moveField(fi, dir) {
      const arr = this.cur.metadata;
      const j = fi + dir;
      if (j < 0 || j >= arr.length) return;
      arr.splice(j, 0, arr.splice(fi, 1)[0]);
    },
    addAllowed(field, e) {
      const val = (e.target.value || "").trim();
      if (!val) return;
      if (!field.allowedValues) this.$set(field, "allowedValues", []);
      if (!field.allowedValues.includes(val)) field.allowedValues.push(val);
      e.target.value = "";
    },
    removeAllowed(field, vi) {
      field.allowedValues.splice(vi, 1);
    },
    save() {
      if (!this.cur) return;
      this.saving = true;
      this.$store.state.saveMetadataSet(this.cur, () => {
        this.saving = false;
      });
    },
  },
};
</script>
